import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
  ThumbUp,
  ThumbDown,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Timesheet, TimesheetStatus, User } from '../types';
import Layout from '../components/Layout/Layout';

const TimesheetApprovals: React.FC = () => {
  const navigate = useNavigate();
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const userIdToUser = React.useMemo(() => {
    const map: Record<string, User> = {};
    users.forEach(u => { if (u?.id) map[u.id] = u; });
    return map;
  }, [users]);
  const [selectedTimesheet, setSelectedTimesheet] = useState<Timesheet | null>(null);
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [rejectionDialog, setRejectionDialog] = useState(false);
  const [approvalType, setApprovalType] = useState<'approve' | 'reject'>('approve');
  const [comments, setComments] = useState('');
  const [reason, setReason] = useState('');
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPendingTimesheets();
    // Load users to map userId -> name for approvals table
    (async () => {
      try {
        const list = await apiService.getAllUsers();
        setUsers(list || []);
      } catch {}
    })();
  }, []);

  const loadPendingTimesheets = async () => {
    try {
      setLoading(true);
      const data = await apiService.getPendingTimesheetsForApproval();
      setTimesheets(data);
    } catch (error) {
      console.error('Failed to load pending timesheets:', error);
      setError('Failed to load pending timesheets');
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async () => {
    if (!selectedTimesheet) return;

    try {
      if (approvalType === 'approve') {
        await apiService.approveTimesheet(selectedTimesheet.id, comments);
        setSuccess('Timesheet approved successfully!');
      } else {
        await apiService.rejectTimesheet(selectedTimesheet.id, reason, comments);
        setSuccess('Timesheet rejected successfully!');
      }

      // Clear form
      setComments('');
      setReason('');
      setApprovalDialog(false);
      setRejectionDialog(false);
      setSelectedTimesheet(null);

      // Reload timesheets
      loadPendingTimesheets();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to process timesheet:', error);
      setError('Failed to process timesheet');
    }
  };

  const openApprovalDialog = (timesheet: Timesheet) => {
    setSelectedTimesheet(timesheet);
    setApprovalType('approve');
    setComments('');
    setApprovalDialog(true);
  };

  const openRejectionDialog = (timesheet: Timesheet) => {
    setSelectedTimesheet(timesheet);
    setApprovalType('reject');
    setComments('');
    setReason('');
    setRejectionDialog(true);
  };

  const getTotalHours = (timesheet: Timesheet) => {
    if (!timesheet.items) return 0;
    const parseHours = (hours: any): number => {
      if (typeof hours === 'string') {
        return parseFloat(hours) || 0;
      }
      return hours || 0;
    };
    
    return timesheet.items.reduce((total, item) => {
      return total + 
        parseHours(item.mondayHours) + 
        parseHours(item.tuesdayHours) + 
        parseHours(item.wednesdayHours) + 
        parseHours(item.thursdayHours) + 
        parseHours(item.fridayHours) + 
        parseHours(item.saturdayHours) + 
        parseHours(item.sundayHours);
    }, 0);
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Timesheet Approvals
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review and approve submitted timesheets
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {timesheets.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" align="center" color="text.secondary">
              No pending timesheets for approval
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary">
              All submitted timesheets have been processed
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Week</TableCell>
                <TableCell>Total Hours</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {timesheets.map((timesheet) => (
                <TableRow key={timesheet.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {timesheet as any && (timesheet as any).user
                        ? `${((timesheet as any).user.firstName || '')} ${((timesheet as any).user.lastName || '')}`.trim()
                        : (userIdToUser[timesheet.userId]
                            ? `${userIdToUser[timesheet.userId].firstName} ${userIdToUser[timesheet.userId].lastName}`
                            : timesheet.userId)
                      }
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {new Date(timesheet.fromDate).toLocaleDateString()} -{' '}
                    {new Date(timesheet.toDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{getTotalHours(timesheet).toFixed(2)} hours</TableCell>
                  <TableCell>
                    <Chip
                      label={timesheet.status}
                      color="warning"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {timesheet.createdAt
                      ? new Date(timesheet.createdAt).toLocaleDateString()
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/timesheets/${timesheet.id}`)}
                        title="View Details"
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => openApprovalDialog(timesheet)}
                        title="Approve"
                      >
                        <ThumbUp />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => openRejectionDialog(timesheet)}
                        title="Reject"
                      >
                        <ThumbDown />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Approval Dialog */}
      <Dialog open={approvalDialog} onClose={() => setApprovalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Timesheet</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to approve this timesheet?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Comments (optional)"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Add any comments about this approval..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog(false)}>Cancel</Button>
          <Button
            onClick={handleApproval}
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={rejectionDialog} onClose={() => setRejectionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Timesheet</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this timesheet.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason for rejection"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                placeholder="e.g., Incorrect hours, missing details..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Additional comments (optional)"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add any additional comments..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectionDialog(false)}>Cancel</Button>
          <Button
            onClick={handleApproval}
            variant="contained"
            color="error"
            startIcon={<Cancel />}
            disabled={!reason.trim()}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default TimesheetApprovals;